import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CheckCircleIcon, XCircleIcon, ExclamationCircleIcon } from '@heroicons/react/solid';
import { ClipLoader } from 'react-spinners';
import { getConfig } from '../config';
import { formatPenCurrency } from '../utils/currency';

const normalizePaymentStatus = (rawStatus) => {
    const normalized = String(rawStatus || '').trim().toUpperCase();

    switch (normalized) {
        case 'APPROVED':
        case 'ACCREDITED':
            return 'APPROVED';
        case 'PENDING':
        case 'PENDING_WAITING_PAYMENT':
        case 'IN_PROCESS':
            return 'PENDING';
        case 'REJECTED':
        case 'DECLINED':
        case 'CANCELLED':
        case 'CANCELED':
            return 'REJECTED';
        default:
            return normalized;
    }
};

const formatOptionalDateTime = (rawValue) => {
    if (!rawValue) {
        return '';
    }

    const parsedDate = new Date(rawValue);
    if (Number.isNaN(parsedDate.getTime())) {
        return String(rawValue);
    }

    return parsedDate.toLocaleString('es-PE', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const ConfirmationPage = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const queryString = location.search;
    const queryParams = new URLSearchParams(queryString);
    const transactionId =
        queryParams.get('id')?.trim() ||
        queryParams.get('payment_id')?.trim() ||
        queryParams.get('collection_id')?.trim() ||
        '';
    const queryStatus = queryParams.get('status')?.trim();
    const queryCollectionStatus = queryParams.get('collection_status')?.trim();
    const { apiBaseURL } = getConfig();

    const [status, setStatus] = useState(null);
    const [paymentInfo, setPaymentInfo] = useState(null);
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const lastFetchedIdRef = useRef(null);

    useEffect(() => {
        let alreadyFetched = false;

        const fetchConfirmation = async () => {
            if (alreadyFetched) {
                return;
            }
            alreadyFetched = true;

            if (lastFetchedIdRef.current === transactionId) {
                return;
            }
            lastFetchedIdRef.current = transactionId;

            try {
                const confirmationParams = new URLSearchParams();
                confirmationParams.set('transactionId', transactionId);

                [
                    'payment_id',
                    'collection_id',
                    'status',
                    'collection_status',
                    'external_reference',
                    'payment_type'
                ].forEach((key) => {
                    const value = new URLSearchParams(queryString).get(key)?.trim();
                    if (value) {
                        confirmationParams.set(key, value);
                    }
                });

                const res = await fetch(
                    `${apiBaseURL}/api/Validation/confirmation?${confirmationParams.toString()}`
                );
                if (!res.ok) {
                    let errorMessage = 'Error al obtener la confirmacion';

                    try {
                        const errorJson = await res.json();
                        errorMessage = errorJson?.message || errorMessage;
                    } catch {
                        // Si la respuesta no es JSON, mantenemos el mensaje por defecto.
                    }

                    throw new Error(errorMessage);
                }

                const json = await res.json();
                const resolvedStatus = normalizePaymentStatus(
                    json?.status ||
                    json?.payload?.status ||
                    queryCollectionStatus ||
                    queryStatus ||
                    json?.paymentInfo?.statusDetail
                );

                setStatus(resolvedStatus);
                setPaymentInfo(json?.paymentInfo || null);
                setInvoices(json?.data || []);
            } catch (requestError) {
                setStatus(normalizePaymentStatus(queryCollectionStatus || queryStatus));
                setError(requestError.message);
            } finally {
                setLoading(false);
            }
        };

        if (transactionId) {
            fetchConfirmation();
        } else {
            setLoading(false);
            setError('ID de transaccion no proporcionado');
        }
    }, [transactionId, apiBaseURL, queryCollectionStatus, queryStatus, queryString]);

    const displayStatus =
        status === 'APPROVED' ? 'APROBADO'
            : status === 'PENDING' ? 'PENDIENTE'
                : status ? 'RECHAZADO'
                    : '';

    const isCashPending =
        status === 'PENDING' && (
            paymentInfo?.paymentMethodId === 'pagoefectivo_atm' ||
            paymentInfo?.paymentTypeId === 'ticket' ||
            paymentInfo?.externalResourceUrl ||
            paymentInfo?.paymentMethodReferenceId
        );

    const paymentReference =
        paymentInfo?.paymentMethodReferenceId ||
        paymentInfo?.barcodeContent ||
        '';
    const paymentLink = paymentInfo?.externalResourceUrl || '';

    const handleReturn = () => navigate('/');

    return (
        <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 sm:px-6 lg:px-8">
            <div className="w-full max-w-lg rounded-xl bg-white p-12 shadow-lg">
                <div className="flex flex-col items-center">
                    {loading && (
                        <div className="mb-8 flex w-full flex-col items-center justify-center space-y-2 text-center">
                            <ClipLoader color="#111827" size={42} />
                            <p className="text-sm text-gray-600">
                                Consultando el estado de la transaccion...
                            </p>
                        </div>
                    )}

                    {status === 'APPROVED' && (
                        <>
                            <CheckCircleIcon className="h-20 w-20 text-green-500" />
                            <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
                                Transaccion Aprobada
                            </h2>
                            <p className="mt-2 text-sm text-gray-600">
                                La transaccion ha sido procesada exitosamente.
                            </p>
                        </>
                    )}
                    {status === 'PENDING' && (
                        <>
                            <ExclamationCircleIcon className="h-20 w-20 text-yellow-500" />
                            <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
                                Transaccion Pendiente
                            </h2>
                            <p className="mt-2 text-sm text-gray-600">
                                El pago se encuentra en estado pendiente. Te notificaremos cuando cambie
                                el estado.
                            </p>
                        </>
                    )}
                    {status && status !== 'APPROVED' && status !== 'PENDING' && (
                        <>
                            <XCircleIcon className="h-20 w-20 text-red-500" />
                            <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
                                Transaccion Rechazada
                            </h2>
                        </>
                    )}

                    <ul className="mt-8 w-full space-y-1 text-left">
                        <li><strong>ID de Transaccion:</strong> {transactionId || '-'}</li>
                        <li><strong>Estado:</strong> {displayStatus || '-'}</li>
                    </ul>

                    {isCashPending && (
                        <div className="mt-6 w-full rounded-xl border border-amber-200 bg-amber-50 p-4 text-left">
                            <h3 className="text-lg font-bold text-amber-900">
                                Datos para completar el pago en efectivo
                            </h3>
                            <p className="mt-2 text-sm text-amber-900">
                                Este pago quedo pendiente en PagoEfectivo. Usa esta referencia para
                                completar el pago.
                            </p>
                            <ul className="mt-3 space-y-1 text-sm text-amber-950">
                                <li><strong>Medio de pago:</strong> PagoEfectivo</li>
                                {paymentReference && (
                                    <li><strong>Numero de convenio:</strong> {paymentReference}</li>
                                )}
                                {paymentInfo?.dateOfExpiration && (
                                    <li>
                                        <strong>Vence:</strong> {formatOptionalDateTime(paymentInfo.dateOfExpiration)}
                                    </li>
                                )}
                            </ul>
                            {paymentLink && (
                                <div className="mt-4">
                                    <button
                                        type="button"
                                        onClick={() => window.open(paymentLink, '_blank', 'noopener,noreferrer')}
                                        className="w-full rounded-lg bg-amber-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-amber-700"
                                    >
                                        Ver instrucciones de pago
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {(status === 'APPROVED' || status === 'PENDING') && (
                        <div className="mt-8 w-full">
                            {loading ? (
                                <p>Cargando facturas...</p>
                            ) : error ? (
                                <p className="text-red-500">Error: {error}</p>
                            ) : invoices.length > 0 ? (
                                <table className="mt-4 min-w-full table-auto">
                                    <thead>
                                        <tr>
                                            <th className="border-b px-4 py-2">Factura No</th>
                                            <th className="border-b px-4 py-2">Monto</th>
                                            <th className="border-b px-4 py-2">Fecha de Pago</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {invoices.map((inv, index) => (
                                            <tr key={index}>
                                                <td className="border-b px-4 py-2">{inv.facturaNumero}</td>
                                                <td className="border-b px-4 py-2">
                                                    {inv.facturaValor != null
                                                        ? formatPenCurrency(inv.facturaValor)
                                                        : formatPenCurrency(0)}
                                                </td>
                                                <td className="border-b px-4 py-2">
                                                    {inv.pagoFecha && !Number.isNaN(new Date(inv.pagoFecha).getTime())
                                                        ? new Date(inv.pagoFecha).toLocaleDateString('es-PE', {
                                                            day: '2-digit',
                                                            month: 'long',
                                                            year: 'numeric',
                                                        })
                                                        : 'Fecha no disponible'}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : (
                                <p className="mt-4 text-center">No hay facturas para esta transaccion.</p>
                            )}
                        </div>
                    )}

                    <div className="mt-8 w-full text-center">
                        {status === 'APPROVED' && (
                            <>
                                <h3 className="text-2xl font-bold text-gray-900">Gracias por su pago</h3>
                                <p className="mt-2 text-sm text-gray-600">
                                    Tu transaccion ha sido procesada exitosamente. Si tienes alguna
                                    pregunta, no dudes en contactarnos.
                                </p>
                            </>
                        )}
                        {status && status !== 'APPROVED' && status !== 'PENDING' && (
                            <p className="text-2xl font-bold text-gray-900">
                                Lo sentimos, pero tu pago no fue procesado.
                            </p>
                        )}
                    </div>

                    {!loading && (
                        <button
                            onClick={handleReturn}
                            className="mt-6 rounded-lg bg-blue-500 px-6 py-3 text-lg font-semibold text-white transition hover:bg-blue-600"
                        >
                            Volver al Inicio
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ConfirmationPage;
